import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { TotalsByDescriptor } from '@/types/service/internalAnalytics';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { CHART_COLORS, formatCurrency } from '@/utils/saasDashboardHelpers';

interface StripeRevenueByDescriptorChartProps {
  data: TotalsByDescriptor[];
}

const StripeRevenueByDescriptorChart = ({ data }: StripeRevenueByDescriptorChartProps) => {
  if (data.length === 0) {
    return (
      <Card className='h-full'>
        <CardHeader>
          <CardTitle className='text-lg'>Revenue by Product Line</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[300px] flex items-center justify-center text-muted-foreground'>
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  // Transform and sort data by net revenue (descending)
  const chartData = data
    .map((item) => ({
      name: item.statement_descriptor || 'Unknown',
      netRevenue: parseFloat(item.total_fee_deducted_correct_amount),
      grossRevenue: parseFloat(item.total_correct_amount),
      fees: parseFloat(item.total_fee),
    }))
    .sort((a, b) => b.netRevenue - a.netRevenue);

  // Custom tooltip
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: any[];
    label?: string;
  }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className='bg-white p-3 border rounded shadow-lg'>
        <p className='font-medium text-sm mb-2'>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className='text-sm' style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  };

  // Calculate dynamic height based on number of items (minimum 300px, 50px per item)
  const chartHeight = Math.max(300, chartData.length * 50);

  // Calculate the longest label to set appropriate width
  const maxLabelLength = Math.max(...chartData.map((item) => item.name.length));
  const yAxisWidth = Math.min(Math.max(maxLabelLength * 7, 120), 250);

  return (
    <Card className='h-full'>
      <CardHeader>
        <CardTitle className='text-lg'>Revenue by Product Line</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width='100%' height={chartHeight}>
          <BarChart
            data={chartData}
            layout='vertical'
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray='3 3' horizontal={true} vertical={false} />
            <XAxis type='number' tickFormatter={(value) => formatCurrency(value)} />
            <YAxis type='category' dataKey='name' width={yAxisWidth} tick={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey='netRevenue'
              name='Net Revenue'
              fill={CHART_COLORS.pie[1]}
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default StripeRevenueByDescriptorChart;
